import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Calendar, FileText, GraduationCap, Phone, Mail, User, TrendingUp, ArrowLeft, Download, CheckCircle } from "lucide-react";
import { useState } from "react";
import { saveAdmissionEntry, exportAdmissionsToExcel, getAdmissionEntries } from "@/lib/exportToExcel";

export default function Admissions() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    gpa: "",
    specialization: "none",
    program: "none"
  });

  const programs = [
    {
      id: "diploma",
      nameAr: "دبلوم متوسط",
      duration: "سنتان",
      fee: "600 دينار ليبي",
      minGrade: "50%",
      color: "from-blue-600 to-blue-800",
      note: "",
      specializations: [
        "هندسة البرمجيات",
        "هندسة الشبكات والاتصالات",
        "إدارة الأعمال",
        "المحاسبة المالية",
        "التمويل والاستثمار",
        "الإدارة والتنظيم القانوني"
      ]
    },
    {
      id: "advanced-diploma",
      nameAr: "دبلوم عالي",
      duration: "3 سنوات",
      fee: "650 دينار ليبي",
      minGrade: "60%",
      color: "from-indigo-600 to-indigo-800",
      note: "",
      specializations: [
        "هندسة البرمجيات",
        "هندسة الشبكات والاتصالات",
        "إدارة الأعمال",
        "المحاسبة المالية",
        "التمويل والاستثمار",
        "الإدارة والتنظيم القانوني"
      ]
    },
    {
      id: "bachelor",
      nameAr: "بكالوريوس",
      duration: "4 سنوات",
      fee: "860 دينار ليبي",
      minGrade: "65%",
      color: "from-amber-600 to-amber-800",
      note: "",
      specializations: [
        "هندسة البرمجيات",
        "هندسة الشبكات والاتصالات",
        "إدارة الأعمال",
        "المحاسبة المالية",
        "التمويل والاستثمار",
        "الإدارة والتنظيم القانوني"
      ]
    },
    {
      id: "bachelor-completion",
      nameAr: "استكمال البكالوريوس",
      duration: "سنة ونصف",
      fee: "950 دينار ليبي",
      minGrade: "حاملو الدبلوم العالي",
      color: "from-emerald-600 to-emerald-800",
      note: "لخريجي الدبلوم العالي فقط",
      specializations: [
        "هندسة البرمجيات",
        "هندسة الشبكات والاتصالات",
        "إدارة الأعمال",
        "المحاسبة المالية",
        "التمويل والاستثمار",
        "الإدارة والتنظيم القانوني"
      ]
    }
  ];

  const admissionSteps = [
    { step: 1, title: "تقديم الطلب", description: "املأ نموذج التسجيل الإلكتروني" },
    { step: 2, title: "تقديم الوثائق", description: "إرفاق المستندات المطلوبة" },
    { step: 3, title: "دفع رسوم التسجيل", description: "دفع رسوم التسجيل الأولية" },
    { step: 4, title: "اختبار القبول", description: "اجتياز اختبار القبول" },
    { step: 5, title: "المقابلة الشخصية", description: "حضور المقابلة الشخصية" },
    { step: 6, title: "القبول النهائي", description: "استلام خطاب القبول" }
  ];

  const requiredDocuments = {
    basic: [
      "شهادة الثانوية العامة الأصلية",
      "صورة من بطاقة الهوية الوطنية",
      "4 صور شخصية حديثة",
      "شهادة الميلاد"
    ],
    medical: [
      "شهادة طبية تثبت اللياقة الصحية",
      "تحليل دم شامل",
      "صورة أشعة للصدر"
    ],
    additional: [
      "شهادة حسن سيرة وسلوك",
      "إيصال دفع رسوم التسجيل",
      "نموذج طلب الالتحاق مكتمل"
    ]
  };

  const importantDates = [
    { date: "2026-05-01", event: "بداية التسجيل" },
    { date: "2026-06-30", event: "نهاية التسجيل" },
    { date: "2026-07-15", event: "اختبارات القبول" },
    { date: "2026-08-01", event: "إعلان النتائج" },
    { date: "2026-09-01", event: "بداية الدراسة" }
  ];

  const [submitted, setSubmitted] = useState(false);
  const [entryCount, setEntryCount] = useState(() => getAdmissionEntries().length);

  const specializationLabels: Record<string, string> = {
    none: '',
    software: 'هندسة البرمجيات',
    network: 'هندسة الشبكات والاتصالات',
    business: 'إدارة الأعمال',
    accounting: 'المحاسبة المالية',
    finance: 'التمويل والاستثمار',
    legal: 'الإدارة والتنظيم القانوني',
  };

  const programLabels: Record<string, string> = {
    none: '',
    diploma: 'دبلوم متوسط',
    'advanced-diploma': 'دبلوم عالي',
    bachelor: 'بكالوريوس',
    'bachelor-completion': 'استكمال البكالوريوس',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveAdmissionEntry({
      الاسم: formData.name,
      الهاتف: formData.phone,
      البريد: formData.email,
      المعدل: formData.gpa,
      التخصص: specializationLabels[formData.specialization] || formData.specialization,
      البرنامج: programLabels[formData.program] || formData.program,
    });
    setEntryCount((c) => c + 1);
    setSubmitted(true);
    setFormData({ name: '', phone: '', email: '', gpa: '', specialization: 'none', program: 'none' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={springPresets.gentle}
          className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        >
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.HERO_UNIVERSITY_3}
              alt="Graduation"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="relative z-10 container mx-auto px-4 text-center">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              <Badge className="bg-accent text-accent-foreground text-lg px-6 py-2">
                التسجيل مفتوح للفصل الربيعي 2026
              </Badge>
              <h1 className="text-5xl md:text-7xl font-bold text-foreground">
                القبول والتسجيل
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                انضم إلى كلية بوابة التكنولوجيا وابدأ رحلتك الأكاديمية نحو مستقبل مشرق
              </p>
            </motion.div>
          </div>
        </motion.section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={staggerItem} className="text-4xl font-bold mb-4">
                البرامج الأكاديمية
              </motion.h2>
              <motion.p variants={staggerItem} className="text-xl text-muted-foreground">
                اختر البرنامج المناسب لطموحاتك الأكاديمية
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {programs.map((program) => (
                <motion.div key={program.id} variants={staggerItem}>
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-primary overflow-hidden group">
                    <div className={`bg-gradient-to-br ${program.color} p-6 text-white`}>
                      <GraduationCap className="w-10 h-10 mb-3 opacity-90" />
                      <h3 className="text-2xl font-bold">{program.nameAr}</h3>
                      <Badge className="mt-2 bg-white/20 text-white border-white/30 text-sm">
                        {program.duration}
                      </Badge>
                      {program.note && (
                        <p className="mt-2 text-xs bg-white/20 rounded-lg px-3 py-1 inline-block">{program.note}</p>
                      )}
                    </div>
                    <CardContent className="space-y-4 pt-5">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-accent">{program.fee}</p>
                        <p className="text-sm text-muted-foreground">/ فصل دراسي</p>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <TrendingUp className="w-4 h-4 flex-shrink-0" />
                        <span>القبول: {program.minGrade}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-sm">التخصصات المتاحة:</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          {program.specializations.map((spec, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                              {spec}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Link
                        to={`/programs/${program.id}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className={`mt-2 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r ${program.color} text-white text-sm font-semibold hover:opacity-90 transition-all`}
                      >
                        تفاصيل البرنامج
                        <ArrowLeft className="w-4 h-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={staggerItem} className="text-4xl font-bold mb-4">
                خطوات التسجيل
              </motion.h2>
              <motion.p variants={staggerItem} className="text-xl text-muted-foreground">
                اتبع هذه الخطوات البسيطة للتسجيل في الكلية
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8"
            >
              {admissionSteps.map((step) => (
                <motion.div key={step.step} variants={staggerItem}>
                  <Card className="text-center h-full hover:shadow-lg transition-all duration-300">
                    <CardHeader>
                      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                        {step.step}
                      </div>
                      <CardTitle className="text-xl">{step.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{step.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={staggerItem} className="text-4xl font-bold mb-4">
                المستندات المطلوبة
              </motion.h2>
              <motion.p variants={staggerItem} className="text-xl text-muted-foreground">
                تأكد من إحضار جميع المستندات التالية
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8"
            >
              <motion.div variants={staggerItem}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-6 h-6 text-primary" />
                      المستندات الأساسية
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {requiredDocuments.basic.map((doc, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-6 h-6 text-primary" />
                      المستندات الطبية
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {requiredDocuments.medical.map((doc, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-6 h-6 text-primary" />
                      مستندات إضافية
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {requiredDocuments.additional.map((doc, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.h2 variants={staggerItem} className="text-4xl font-bold mb-4">
                التواريخ المهمة
              </motion.h2>
              <motion.p variants={staggerItem} className="text-xl text-muted-foreground">
                احتفظ بهذه التواريخ في جدولك
              </motion.p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl mx-auto space-y-4"
            >
              {importantDates.map((item, idx) => (
                <motion.div key={idx} variants={staggerItem}>
                  <Card className="hover:shadow-lg transition-all duration-300">
                    <CardContent className="flex items-center justify-between p-6">
                      <div className="flex items-center gap-4">
                        <Calendar className="w-8 h-8 text-primary" />
                        <div>
                          <p className="font-semibold text-lg">{item.event}</p>
                          <p className="text-muted-foreground">{item.date}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-base">
                        {new Date(item.date).toLocaleDateString('ar-LY', { month: 'long', day: 'numeric' })}
                      </Badge>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <motion.div variants={staggerItem} className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">نموذج التسجيل السريع</h2>
                <p className="text-xl text-muted-foreground">
                  املأ النموذج وسنتواصل معك قريباً
                </p>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card>
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          الاسم الكامل
                        </Label>
                        <Input
                          id="name"
                          placeholder="أدخل اسمك الكامل"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          رقم الهاتف
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="0912345678"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          البريد الإلكتروني
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="example@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="gpa" className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          المعدل التراكمي
                        </Label>
                        <Input
                          id="gpa"
                          placeholder="مثال: 75%"
                          value={formData.gpa}
                          onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="specialization">
                          التخصص المرغوب
                        </Label>
                        <Select
                          value={formData.specialization}
                          onValueChange={(value) => setFormData({ ...formData, specialization: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="اختر التخصص" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">اختر التخصص</SelectItem>
                            <SelectItem value="software">هندسة البرمجيات</SelectItem>
                            <SelectItem value="network">هندسة الشبكات والاتصالات</SelectItem>
                            <SelectItem value="business">إدارة الأعمال</SelectItem>
                            <SelectItem value="accounting">المحاسبة المالية</SelectItem>
                            <SelectItem value="finance">التمويل والاستثمار</SelectItem>
                            <SelectItem value="legal">الإدارة والتنظيم القانوني</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="program">
                          البرنامج الأكاديمي
                        </Label>
                        <Select
                          value={formData.program}
                          onValueChange={(value) => setFormData({ ...formData, program: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="اختر البرنامج" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">اختر البرنامج</SelectItem>
                            <SelectItem value="diploma">دبلوم متوسط (سنتان) - 600 دينار</SelectItem>
                            <SelectItem value="advanced-diploma">دبلوم عالي (3 سنوات) - 650 دينار</SelectItem>
                            <SelectItem value="bachelor">بكالوريوس (4 سنوات) - 860 دينار</SelectItem>
                            <SelectItem value="bachelor-completion">استكمال البكالوريوس لخريجي الدبلوم العالي - 950 دينار</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {submitted ? (
                        <div className="flex items-center justify-center gap-3 w-full py-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
                          <CheckCircle className="w-6 h-6" />
                          <span className="font-semibold text-lg">تم إرسال طلبك بنجاح! سنتواصل معك قريباً</span>
                        </div>
                      ) : (
                        <Button type="submit" className="w-full text-lg py-6">
                          إرسال الطلب
                        </Button>
                      )}
                    </form>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Excel Export Button */}
              {entryCount > 0 && (
                <motion.div variants={staggerItem} className="mt-6 text-center">
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                    <p className="text-green-700 font-semibold mb-1">📋 عدد الطلبات المحفوظة: <span className="text-2xl font-bold">{entryCount}</span></p>
                    <p className="text-green-600 text-sm mb-4">يمكنك تحميل جميع طلبات التسجيل كملف Excel</p>
                    <Button
                      onClick={exportAdmissionsToExcel}
                      className="bg-green-600 hover:bg-green-700 text-white gap-2"
                    >
                      <Download className="w-5 h-5" />
                      تحميل بيانات الطلبات (Excel)
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}